import React, { Fragment, useState, useContext, useEffect, Dispatch, SetStateAction } from 'react';
import classnames from 'classnames';
import { Header, Loading } from '@/components';
import { i18n, getToken7n, uuid, upload7nB64, base64ToBlob, sceenGt768, priceFormat } from '@/utils';
import { ProductContext } from '.';
import { createShare } from './share-pattern';
import Icon from './share-icon';
import ShareGiftSVG from './share-gift-svg';
import ShareLinks from './share-links';
import styles from './share.less';

export enum ShareType {
  NotShare, // 未分享
  Shared, // 已分享
}

interface ShareProps {
  /** 分享链接 */
  shareLink?: string;
  /** 分享文案 */
  shareText?: string;
  /** 分享优惠价格 */
  sharePrice?: number;
  /** 分享优惠券图案 */
  shareCoupon?: string;
  /** 分享事件分类 */
  shareEventCategory?: string;
  /** 用户头像图片 */
  avatarImage: string;
  /** 壁纸图片 */
  wallpaperImage: string;
  /** 袜子背景色 */
  sockColor: string;
  /** 价格 */
  price: string;
  /** gtag 统计 */
  gtag: any;
  /** 袜子图片导出 */
  // sockImageExport: () => string;
  /** 设置分享状态 */
  setIsShared: Dispatch<SetStateAction<boolean>>;
  /** 关闭回调 */
  onClose: () => void;
}

const Share: React.FC<ShareProps> = ({
  shareLink,
  shareText,
  sharePrice,
  shareCoupon,
  shareEventCategory,
  avatarImage,
  wallpaperImage,
  // backgroundColor,
  sockColor,
  price,
  gtag,
  // sockImageExport,
  setIsShared,
  onClose,
}) => {

  /** 用户上传的图片集合 */
  const { shop, images } = useContext(ProductContext);
  /** 分享链接弹窗显影 */
  const [ shareLinksVisible, setShareLinksVisible ] = useState<boolean>(false);
  /** 分享状态 */
  const [ shareStatus, setShareStatus ] = useState<ShareType>(ShareType.NotShare);
  /** 分享图片 */
  const [ shareImageURL, setShareImageURL ] = useState<{ url: string, base64: string }>({
    url: '',
    base64: '',
  });
  /** 分享加载 */
  const [ shareLoading, setShareLoading ] = useState<boolean>(false);
  /** 分享前价格 */
  const originPrice = (parseFloat(price) + sharePrice!).toFixed(2);
  /** 分享头像壁纸的 blob 对象 URL */
  const avatarBlobURL = URL.createObjectURL(base64ToBlob(avatarImage));
  const wallpaperBlobURL = URL.createObjectURL(base64ToBlob(wallpaperImage));

  /** 生成图片 */
  const createImage = async () => {
    const stime = new Date().getTime();
    const [ shareImage, token ] = await Promise.all([
      createShare(images[0], sockColor),
      getToken7n(),
    ]);
    const encodeKey = `new-sock/share/${uuid()}`;
    const response = await upload7nB64({ base64: shareImage, token, key: `${encodeKey}-share.png` });
    setShareImageURL({
      url: response.key.split('new-sock/share/')[1],
      base64: shareImage,
    });
    gtag('event', 'timing_complete', {
      'event_category': shareEventCategory || 'newsock_share',
      'name': 'create_share_image',
      'value': new Date().getTime() - stime,
    });
  }

  useEffect(() => {
    if (shareLinksVisible === true && !shareImageURL.url) {
      setTimeout(createImage, 320);
      gtag('event', `sunzi_share_open`, {
        'event_category': shareEventCategory || 'newsock_share'
      });
    }
    return () => {
      URL.revokeObjectURL(avatarBlobURL);
      URL.revokeObjectURL(wallpaperBlobURL);
    }
  }, [ shareLinksVisible ]);

  useEffect(() => {
    if (shareStatus === ShareType.Shared) {
      setIsShared(true);
    }
  }, [ shareStatus ]);

  /** 分享跳过 */
  const handleClose = () => {
    gtag('event', `sunzi_share_skip`, {
      'event_category': shareEventCategory || 'newsock_share'
    });
    onClose();
  }

  /** 点击下载头像 */
  const handleAvatarDownload = () => {
    gtag('event', `sunzi_share_download_avatar`, {
      'event_category': shareEventCategory || 'newsock_share'
    });
  }

  /** 点击下载壁纸 */
  const handleWallpaperDownload = () => {
    gtag('event', `sunzi_share_download_wallpaper`, {
      'event_category': shareEventCategory || 'newsock_share'
    });
  }

  return (
    <div className={classnames(styles.share, {
      [styles.disableEvent]: shareLoading,
    })}>
      <Header
        className={styles.header}
        closeIconType="down"
        closeIconColor="#000"
        onClose={onClose}
      />
      {shareStatus === ShareType.NotShare ?
        <Fragment>
          <div className={styles.title}>{i18n.format('modules.new.sock.share.title')}</div>
          <div className={styles.shareGiftWrap}>
            <div className={styles.shareGiftWrapLayout}>
              <ShareGiftSVG avatarImage={avatarImage} />
              <img className={styles.shareCoupon} src={shareCoupon} />
              <div
                className={styles.wallpaper}
                style={{
                  backgroundImage: `url(${wallpaperImage})`
                }}
              />
            </div>
          </div>
          <div className={styles.shareTips}>
            <div className={styles.tip}>
              <div className={styles.tipNumber}>1</div>
              <div className={styles.tipText}>{i18n.format('modules.new.sock.social.avatar')}</div>
              <div className={styles.tipTag}>{i18n.format('modules.new.sock.free.download')}</div>
            </div>
            <div className={styles.tip}>
              <div className={styles.tipNumber}>2</div>
              <div className={styles.tipText}>
                {sceenGt768 ? i18n.format('modules.new.sock.new.pc.wallpaper') : i18n.format('modules.new.sock.new.mobile.wallpaper')}
              </div>
              <div className={styles.tipTag}>{i18n.format('modules.new.sock.free.download')}</div>
            </div>
            <div className={styles.tip}>
              <div className={styles.tipNumber}>3</div>
              {/* <div className={styles.tipText}>{i18n.format('modules.new.sock.discount.price', `${shop.currencySymbol}${sharePrice}`)}</div> */}
              <div className={styles.tipText}>{i18n.format('modules.new.sock.discount.price', `${priceFormat(shop.currencySymbol, sharePrice!)}`)}</div>
              <div className={styles.tipTag}>{i18n.format('modules.new.sock.discount')}</div>
            </div>
          </div>
          <div className={styles.shareButtonLayout}>
            <div
              className={styles.buttonShare}
              onClick={() => setShareLinksVisible(true)}
            >{i18n.format('modules.new.sock.share.now')}</div>
            <div
              className={styles.buttonWithout}
              onClick={handleClose}
            >
              {i18n.format('modules.new.sock.no.thanks')}
            </div>
          </div>
        </Fragment> :
        <Fragment>
          <div className={styles.sharedTitle}>{i18n.format('modules.new.sock.shared.title')}</div>
          <div className={styles.sharedGiftWrap}>
            <div className={styles.sharedGiftWrapLayout}>
              <ShareGiftSVG avatarImage={avatarImage} />
              <img className={styles.shareCoupon} src={shareCoupon} />
              <div
                className={styles.wallpaper}
                style={{
                  backgroundImage: `url(${wallpaperImage})`
                }}
              />
            </div>
          </div>
          <div className={styles.sharedTips}>
            <div className={styles.tip}>
              <div className={styles.tipNumber}>1</div>
              <div className={styles.tipText}>{i18n.format('modules.new.sock.social.avatar')}</div>
              <a
                className={styles.downloadButton}
                target="_blank"
                href={avatarBlobURL}
                download="avatar"
                onClick={handleAvatarDownload}
              >
                <Icon type="download" color="#ff533a" size={16} />
                <span>{i18n.format('modules.new.sock.download')}</span>
              </a>
            </div>
            <div className={styles.tip}>
              <div className={styles.tipNumber}>2</div>
              <div className={styles.tipText}>
                {sceenGt768 ? i18n.format('modules.new.sock.new.pc.wallpaper') : i18n.format('modules.new.sock.new.mobile.wallpaper')}
              </div>
              <a
                className={styles.downloadButton}
                target="_blank"
                href={wallpaperBlobURL}
                download="wallpaper"
                onClick={handleWallpaperDownload}
              >
                <Icon type="download" color="#ff533a" size={16} />
                <span>{i18n.format('modules.new.sock.download')}</span>
              </a>
            </div>
            <div className={styles.tip}>
              <div className={styles.tipNumber}>3</div>
              {/* <div className={styles.tipText}>{i18n.format('modules.new.sock.discount.price', `${shop.currencySymbol}${sharePrice}`)}</div> */}
              <div className={styles.tipText}>{i18n.format('modules.new.sock.discount.price', `${priceFormat(shop.currencySymbol, sharePrice!)}`)}</div>
              <div className={styles.tipTag}>{i18n.format('modules.new.sock.discount')}</div>
              <div className={styles.price}>
                {/* <div className={styles.nowPrice}>{`${shop.currencySymbol}${price}`}</div> */}
                <div className={styles.nowPrice}>{priceFormat(shop.currencySymbol, price)}</div>
                {/* <div className={styles.oldPrice}>{`${shop.currencySymbol}${originPrice}`}</div> */}
                <div className={styles.oldPrice}>{priceFormat(shop.currencySymbol, originPrice)}</div>
              </div>
            </div>
          </div>
          <div
            className={styles.addCartButton}
            onClick={onClose}
          >
            {i18n.format('modules.global.add.cart')}
          </div>
        </Fragment>
      }
      {shareLinksVisible &&
        <ShareLinks
          shareLink={shareLink}
          shareImageURL={shareImageURL}
          shareText={shareText!}
          shareEventCategory={shareEventCategory}
          gtag={gtag}
          setShareStatus={setShareStatus}
          setShareLoading={setShareLoading}
          onClose={() => setShareLinksVisible(false)}
        />
      }
      {shareLoading &&
        <div className={styles.shareLoading}>
          <Loading color="#fff" size={50} />
        </div>
      }
    </div>
  )
}

export default Share;
